'use client'

import React from 'react'
import Image from 'next/image'
import ErrorComponent from 'src/app/error'

import { LoadingSpinner } from '@fsg/gui-bits'
import { AlertCircle } from '@fsg/icons'

import { cn } from '@app/utils'

import { ENDPOINTS } from '@app/constants'
import { SalesActivityResponse } from '@app/types/reports'

import { Flag } from '../../Flag'
import { KPICard } from '../KPICard'
import { HotList } from './HotList'
import { PastDueList } from './PastDueList'
import { ScheduledList } from './ScheduledList'
import { useRequestOnMount } from '@fsg/next-auth'

// import { StaleList } from './StaleList'

type Props = {
  contactId: string
}

const styles = {
  grid: {
    wrapper: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[16px]',
    header: 'text-[20px] font-bold mb-[12px]',
    column: 'flex flex-col gap-[12px]',
  },
  kpiGrid: 'grid grid-cols-2 gap-sm',
}

export default function SalesActivityDashboard(props: Props) {
  const { data, status, error } = useRequestOnMount<SalesActivityResponse>({
    path: ENDPOINTS.REPORTS.SALES_ACTIVITY(props.contactId),
  })

  if (props.contactId === undefined) return <p className="text-error">Cannot determine your user contact</p>
  if (error || status === 'error')
    return <ErrorComponent error={new Error('Encountered an error getting your sales activity report.')} reset={() => document.location.reload()} />
  if (status === 'loading') return <LoadingSpinner />
  if (!data) return null

  const overviewKPIs: React.ComponentProps<typeof KPICard>[] = [
    {
      label: "Past Due Follow Ups",
      primary: (
        <span className={cn({ "text-red-700": data.leads.pastDue > 0 })}>
          {data.leads.pastDue + data.opportunities.pastDue}{" "}
          {data.leads.pastDue + data.opportunities.pastDue > 0 ? (
            <AlertCircle className="inline text-sm text-red-800" />
          ) : null}
        </span>
      ),
      className: "col-span-2",
      mode: data.leads.pastDue > 0 ? "warning" : undefined,
      icon: (
        <div
          className={cn("h-[48px] w-[48px] rounded-full bg-gray-400 p-2", {
            "bg-red-300": data.leads.pastDue > 0,
            "bg-gray-400": data.leads.pastDue === 0,
          })}
        >
          <Image
            alt="calendar icon"
            width={32}
            height={32}
            style={{ width: "auto", height: "32px" }}
            src={
              data.leads.pastDue > 0 ? "/calendar-alert.png" : "/calendar.png"
            }
          />
        </div>
      ),
    },
    {
      label: "Scheduled Follow Ups · 7D",
      primary: getKPIPrimary(data.leads.followups),
    },
    {
      label: "Stale Leads · > 14D",
      primary: getKPIPrimary(data.leads.stale.current),
      flag: getFlag(data.leads.stale.changePct, "neg"),
    },
    {
      label: "New Leads · 30D",
      primary: getKPIPrimary(data.leads.newLeads.current),
      flag: getFlag(data.leads.newLeads.changePct, "pos"),
    },
    {
      label: "New Accounts · 30D",
      primary: getKPIPrimary(data.accounts.newAccounts.current),
      flag: getFlag(data.accounts.newAccounts.changePct, "pos"),
    },
    {
      label: "Lead Conversion · 30D",
      primary: getKPIPrimary(data.leads.converted.current),
      flag: getFlag(data.leads.converted.changePct, "pos"),
    },
    {
      label: "Lead Conversion % · 30D",
      primary: getKPIPrimary(
        data.leads.conversionRate.current * 100,
        "percent"
      ),
      flag: getFlag(data.leads.conversionRate.changePct, "pos"),
    },
    {
      label: "Opportunity Close · 30D",
      primary: (
        <span className="flex items-center">
          {data.opportunities.won !== null ? (
            <span>
              {data.opportunities.won}{" "}
              <span className="text-[14px] font-bold">won</span>
            </span>
          ) : (
            "N/A"
          )}
          <div className="mx-[6px] h-[16px] border-r-[1px] border-gray-500" />
          {data.opportunities.lost !== null ? (
            <span>
              {data.opportunities.lost}{" "}
              <span className="text-[14px] font-bold">lost</span>
            </span>
          ) : (
            "N/A"
          )}
        </span>
      ),
    },
    {
      label: "Opportunity Close % · 30D",
      primary: getKPIPrimary(
        data.opportunities.closeRate.current * 100,
        "percent"
      ),
      flag: getFlag(data.opportunities.closeRate.changePct, "pos"),
    },
  ]

  return (
    <div className={styles.grid.wrapper}>
      <div>
        <h3 className={styles.grid.header}>Overview</h3>
        <div className={styles.kpiGrid}>
          {overviewKPIs.map((props, key) => (
            <KPICard {...props} key={key} />
          ))}
        </div>
      </div>
      <div>
        <h3 className={styles.grid.header}>Follow Ups</h3>
        <div className={styles.grid.column}>
          <PastDueList />
          <ScheduledList />
        </div>
      </div>
      <div>
        <h3 className={styles.grid.header}>Activity</h3>
        <div className={styles.grid.column}>
          <HotList />
          {/* <StaleList /> */}
        </div>
      </div>
    </div>
  )
}

function getKPIPrimary(value: number | null, format?: 'percent') {
  if (value === null) return 'N/A'
  if (format === 'percent') return `${value.toFixed(2)}%`
  return value
}

function getFlag(value: number | null, rule: 'pos' | 'neg', props?: Partial<React.ComponentProps<typeof Flag>>) {
  // By returning undefined, the KPI card will not render a Flag component
  if (value === null || value === 0) return undefined
  return {
    label: getFlagLabel(value),
    color: getFlagColor(value, rule),
    arrow: getArrowDirection(value),
    ...props,
  }
}

function getFlagLabel(value: number | null) {
  if (value === null) return 'N/A'
  return value.toFixed(2)
}

function getFlagColor(value: number, rule: 'pos' | 'neg'): 'red' | 'green' | undefined {
  if (value === null || value === 0) return undefined

  // if rule === 'pos', the flag should be green when the value is a positive number
  // Think, stale leads are bad when positive
  if (rule === 'pos') return value > 0 ? 'red' : 'green'

  // If rule === 'pos', the flag should be green when the value is a negative number
  // Think, stale leads are good when negative
  if (rule === 'neg') return value > 0 ? 'green' : 'red'
}

function getArrowDirection(value: number): 'up' | 'down' | undefined {
  if (value === null || value === 0) return undefined
  return value > 0 ? 'up' : 'down'
}
